'use no memo'

import { useMemo } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ProjectSavedViewShell } from '../components/ProjectSavedViewShell'
import type { ProjectDocument } from '@/lib/db/rxdb/schemas/project'
import type { ProjectTabValue } from '../types'
import { useProjects } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'
import { Loading } from '@/components/app/indicators/Loading'
import { useViewHydrator } from '@/routes/features/views/useViewHydrator'

export const Route = createFileRoute(
  '/_AppWrapper/_AppLayout/$workspaceSlug/projects/$projectKey/views/$viewId',
)({
  component: ProjectViewPage,
})

function ProjectViewPage() {
  const {
    workspaceSlug,
    projectKey: projectKeyParam,
    viewId,
  } = Route.useParams()
  const projectKey = projectKeyParam.toUpperCase()
  const navigate = useNavigate()
  const workspaceId = useLoginStore((s) => s.workspaceId)

  const { projects, isLoading: projectsLoading } = useProjects()

  const project = useMemo<ProjectDocument | null>(() => {
    return (
      projects.find((entry) => {
        if (entry.isDeleted) return false
        if (entry.projectKey !== projectKey) return false
        if (workspaceId && entry.envId !== workspaceId) return false
        return true
      }) ?? null
    )
  }, [projects, projectKey, workspaceId])

  const { status, view } = useViewHydrator({
    viewId,
    scopeEntityId: project?.id,
    scope: 'project',
  })

  const handleTabChange = (tab: ProjectTabValue) => {
    if (tab === 'views') {
      navigate({
        to: '/$workspaceSlug/projects/$projectKey/views',
        params: { workspaceSlug, projectKey: projectKeyParam },
      })
      return
    }
    navigate({
      to: '/$workspaceSlug/projects/$projectKey',
      params: { workspaceSlug, projectKey: projectKeyParam },
      search: tab === 'tasks' ? {} : { projectTab: tab },
    })
  }

  if (projectsLoading || status === 'pending') {
    return (
      <div className="flex min-h-full flex-1 items-center justify-center">
        <Loading />
      </div>
    )
  }

  if (!project) return null

  return (
    <ProjectSavedViewShell
      projectId={project.id}
      status={status}
      view={view}
      onTabChange={handleTabChange}
      onBackToViews={() =>
        navigate({
          to: '/$workspaceSlug/projects/$projectKey/views',
          params: { workspaceSlug, projectKey: projectKeyParam },
        })
      }
    />
  )
}
